import React, { useMemo, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Form } from "@formio/react";
import TemplateSvgPreview from "../../../pages/CertificateOnboarding/configure/TemplateSvgPreview";
import { CARD_CLASS } from "../utils/styles";
import { prepareReadOnlyFormPreview } from "../utils/preview";

function CertificatePreviewStep({ updateData, t, certificatePreview }) {
  const [activeFormTab, setActiveFormTab] = useState("subject");

  const subjectFieldCount = useMemo(
    () => Object.keys(updateData?.subject_json || {}).length,
    [updateData?.subject_json],
  );

  const templateFieldCount = useMemo(
    () => Object.keys(updateData?.template_json || {}).length,
    [updateData?.template_json],
  );

  const subjectPrepared = useMemo(
    () => prepareReadOnlyFormPreview(updateData?.subject_json, t),
    [updateData?.subject_json, t],
  );

  const templatePrepared = useMemo(
    () => prepareReadOnlyFormPreview(updateData?.template_json, t),
    [updateData?.template_json, t],
  );

  const hasAnyFormSchema = Boolean(
    subjectPrepared.schema || templatePrepared.schema,
  );

  const activePrepared =
    activeFormTab === "subject" ? subjectPrepared : templatePrepared;

  return (
    <div id="cert-onboarding-preview-step" className="mx-auto space-y-5">
      <div
        className={`${CARD_CLASS} px-4 py-4 sm:px-6 sm:py-5 border-l-4 border-l-cfPrimary bg-cfPrimaryMuted`}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-full bg-cfPrimaryMuted flex items-center justify-center">
            <CheckCircle2
              className="w-5 h-5 text-cfPrimary"
              strokeWidth={2}
              aria-hidden
            />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <h2 className="text-base font-semibold text-cfHeadingText">
              {t("certificateOnboarding:preview_ready_title")}
            </h2>
            <p className="text-sm text-cfGlobalText leading-relaxed">
              {t("certificateOnboarding:preview_readonly_hint")}
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              {subjectFieldCount > 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-cfPrimaryMuted text-cfPrimary border border-cfPrimaryBorder">
                  {t("certificateOnboarding:preview_subject_fields_count", {
                    count: subjectFieldCount,
                  })}
                </span>
              )}
              {templateFieldCount > 0 && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-cfSecondary text-cfGlobalText border border-cfCardBorder">
                  {t("certificateOnboarding:preview_template_fields_count", {
                    count: templateFieldCount,
                  })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        {certificatePreview || (
          <TemplateSvgPreview
            templateSvgUrl={updateData?.template_svg_url}
            previewSvgUrl={updateData?.preview_svg_url}
            t={t}
          />
        )}

        <div className={`${CARD_CLASS} p-4 sm:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-sm font-semibold text-cfHeadingText">
              {t("certificateOnboarding:forms_preview_title")}
            </h3>

            <div className="inline-flex self-start rounded-lg bg-cfSecondary border border-cfCardBorder">
              <button
                type="button"
                onClick={() => setActiveFormTab("subject")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary ${
                  activeFormTab === "subject"
                    ? "bg-cfPrimaryMuted text-cfPrimary shadow-sm"
                    : "text-cfGlobalText hover:text-cfHeadingText"
                }`}
              >
                {t("certificateOnboarding:forms_preview_subject_tab")}
              </button>
              <button
                type="button"
                onClick={() => setActiveFormTab("template")}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary ${
                  activeFormTab === "template"
                    ? "bg-cfPrimaryMuted text-cfPrimary shadow-sm"
                    : "text-cfGlobalText hover:text-cfHeadingText"
                }`}
              >
                {t("certificateOnboarding:forms_preview_template_tab")}
              </button>
            </div>
          </div>

          {!hasAnyFormSchema ? (
            <div className="rounded-xl border border-dashed border-cfCardBorder bg-cfSecondary px-4 py-6 text-center text-sm text-cfGlobalText">
              {t("certificateOnboarding:forms_preview_empty")}
            </div>
          ) : activePrepared.schema ? (
            <div
              id="cert-onboarding-form-preview"
              className="max-h-[70vh] overflow-auto pr-1"
            >
              <Form
                form={activePrepared.schema}
                submission={activePrepared.submission}
                options={{ readOnly: true, noAlerts: true }}
              />
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-cfCardBorder bg-cfSecondary px-4 py-6 text-center text-sm text-cfGlobalText">
              {t("certificateOnboarding:forms_preview_empty")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CertificatePreviewStep;
